/**
 * Internationalization (i18n) contract — single source of truth for the
 * locales the template supports end to end (frontend UI, user preference,
 * RBAC labels, transactional emails).
 *
 * Adding a language: extend SUPPORTED_LOCALES here, then follow the
 * "add a language" checklist in docs/guides/I18N.md (locale catalogs, RBAC
 * labels, mail catalog). FR/IT/DA strings are model-generated — have a native
 * speaker review them before production.
 */

/** Locales supported across the whole stack. */
export const SUPPORTED_LOCALES = ["en", "fr", "it", "da"] as const;

/** A supported locale code (`"en" | "fr" | "it" | "da"`). */
export type Locale = (typeof SUPPORTED_LOCALES)[number];

/** Fallback locale when none is set or a value is unrecognized. */
export const DEFAULT_LOCALE: Locale = "en";

/** Human-readable, self-referencing language names for switchers. */
export const LOCALE_LABELS: Record<Locale, string> = {
  en: "English",
  fr: "Français",
  it: "Italiano",
  da: "Dansk",
};

/** Type guard: is the value one of the supported locales? */
export function isSupportedLocale(value: unknown): value is Locale {
  return (
    typeof value === "string" &&
    (SUPPORTED_LOCALES as readonly string[]).includes(value)
  );
}

/**
 * Coerce an arbitrary value (preference, header, env) to a supported locale,
 * falling back to DEFAULT_LOCALE. Accepts region subtags (e.g. "fr-FR" → "fr").
 */
export function resolveLocale(value: unknown): Locale {
  if (isSupportedLocale(value)) return value;
  if (typeof value === "string") {
    const base = value.split("-")[0]?.toLowerCase();
    if (isSupportedLocale(base)) return base;
  }
  return DEFAULT_LOCALE;
}
